import { FC, useState, useEffect } from 'react'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import {
  getCompanyGroupAdmins,
  getVesselsByCompany,
  updateAccountingAccount
} from '../core/_requests'
import {
  CompanyGroupAdmin,
  Vessel,
  AccountingAccount
} from '../core/_models'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope, libraryVesselScopeLocked } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditAccountingCodeModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  accountingCode: AccountingAccount | null
}

const EditAccountingCodeModal: FC<EditAccountingCodeModalProps> = ({ visible, onClose, onSubmit, accountingCode }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
  )
  const vesselScopeLocked = libraryVesselScopeLocked(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const tenantCgaId = procurementTenantCgaId(currentUser)
  const effectiveCgaId = libraryScopeCompany ? tenantCgaId : undefined

  const [formData, setFormData] = useState({
    accountCodes: '',
    company: '',
    vesselId: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // New state for dropdown data
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  // Load accounting code data when modal opens
  useEffect(() => {
    if (visible && accountingCode) {
      setFormData({
        accountCodes: accountingCode.accountCodes || '',
        company: typeof accountingCode.cgaid === 'object' ? accountingCode.cgaid?.id?.toString() || '' : accountingCode.cgaid?.toString() || '',
        vesselId: accountingCode.vesselId ? String(accountingCode.vesselId) : ''
      })
    }
  }, [visible, accountingCode])

  // Load companies when modal opens (only for superadmin)
  useEffect(() => {
    if (visible && libraryScopeAll) {
      loadCompanies()
    } else if (visible && libraryScopeCompany) {
      loadVesselsForCurrentUser()
    }
  }, [visible, libraryScopeAll, libraryScopeCompany])

  // Load vessels for current user (CGA)
  const loadVesselsForCurrentUser = async () => {
    try {
      const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
      if (companyUsername) {
        const vesselsData = await getVesselsByCompany(companyUsername)
        setVessels(vesselsData)
      }
    } catch (error) {
      console.error('Error loading vessels for current user:', error)
      setVessels([])
    }
  }

  // Load vessels when company is selected
  useEffect(() => {
    if (formData.company) {
      const selectedCompany = companies.find(c => c.id === parseInt(formData.company))
      if (selectedCompany) {
        loadVessels(selectedCompany.username)
      }
    } else {
      setVessels([])
    }
  }, [formData.company, companies])

  const loadCompanies = async () => {
    setIsLoadingDropdowns(true)
    try {
      const companiesData = await getCompanyGroupAdmins()
      setCompanies(companiesData)
    } catch (error) {
      console.error('Error loading companies:', error)
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const loadVessels = async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      const vesselsData = await getVesselsByCompany(companyUsername)
      if (vesselsData && vesselsData.length > 0) {
        setVessels(vesselsData)
      } else {
        setVessels([])
      }
    } catch (error) {
      console.error('Error loading vessels:', error)
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}
    if (!formData.accountCodes.trim()) newErrors.accountCodes = 'Account Code is required'

    if (libraryScopeAll && !formData.company) {
      newErrors.company = 'Company is required'
    }
    if (vesselScopeLocked && !formData.vesselId) {
      newErrors.vesselId = 'Vessel is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm() || !accountingCode) return
    setIsSubmitting(true)
    try {
      const selectedVessel = formData.vesselId
        ? vessels.find((v) => v.id === Number(formData.vesselId))
        : undefined
      await updateAccountingAccount(accountingCode.id, {
        accountCodes: formData.accountCodes,
        vesselId: formData.vesselId ? Number(formData.vesselId) : undefined,
        vesselType: selectedVessel?.vesselType,
      })
      toast.success('Accounting Code updated successfully!')
      onSubmit()
      handleClose()
    } catch (error: any) {
      console.error('Error updating accounting code:', error)
      toast.error('Failed to update accounting code')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({ accountCodes: '', company: '', vesselId: '' })
    setErrors({})
    setCompanies([])
    setVessels([])
    onClose()
  }

  if (!visible || !accountingCode) return null

  return (
    <div className="modal fade show d-flex align-items-center justify-content-center" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1050 }}>
      <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Edit Accounting Code</h5>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>
          <div className='modal-body'>
            <div className='row g-3'>
              {/* Company dropdown - only for superadmin */}
              {libraryScopeAll && (
                <div className='col-md-6'>
                  <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                  <SearchableSelect
                    className={`form-select ${errors.company ? 'is-invalid' : ''}`}
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Select Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </SearchableSelect>
                  {errors.company && <div className='invalid-feedback'>{errors.company}</div>}
                </div>
              )}

              <div className='col-12'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Account Code</label>
                <input
                  type='text'
                  className={`form-control ${errors.accountCodes ? 'is-invalid' : ''}`}
                  name='accountCodes'
                  value={formData.accountCodes}
                  onChange={handleInputChange}
                  placeholder='e.g., 6000-EXP-01'
                  style={{ color: '#000' }}
                />
                {errors.accountCodes && <div className='invalid-feedback'>{errors.accountCodes}</div>}
              </div>

              <div className='col-12'>
                <label
                  className={`form-label fw-semibold fs-6 mb-2 ${vesselScopeLocked ? 'required' : ''}`}
                  style={{ color: '#181C32' }}
                >
                  {vesselScopeLocked ? 'Vessel *' : 'Vessel (Optional)'}
                </label>
                <SearchableSelect
                  className={`form-select ${errors.vesselId ? 'is-invalid' : ''}`}
                  name='vesselId'
                  value={formData.vesselId}
                  onChange={handleInputChange}
                  disabled={libraryScopeAll ? !formData.company : false}
                >
                  <option value=''>{vesselScopeLocked ? 'Select Vessel' : 'All Vessels (shared)'}</option>
                  {libraryScopeAll ? (
                    formData.company ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                      ))
                    ) : (
                      <option disabled>Please select a company first</option>
                    )
                  ) : (
                    vessels.length > 0 ? (
                      vessels.map(vessel => (
                        <option key={vessel.id} value={vessel.id}>{vessel.fleet_name}</option>
                      ))
                    ) : (
                      <option disabled>No vessels found</option>
                    )
                  )}
                </SearchableSelect>
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              {/* Show current CGA info for Company Group Admin users */}
              {libraryScopeCompany && effectiveCgaId && (
                <div className='col-12'>
                  <div className='alert alert-info'>
                    <i className='fas fa-info-circle me-2'></i>
                    Accounting code will be updated under your Company Group Admin (ID: {effectiveCgaId})
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>Cancel</button>
            <button type='button' className='btn btn_primary' onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update Accounting Code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditAccountingCodeModal
